<template>
    <page title="competitionApprovalCategorryArt" :textHeight="(WindowSize.y - 276) + 'px'">
        <template #pageContainer>
			<v-form ref="form" v-model="valid">
			<v-row><v-col>
				<c-checkbox v-model="startPassOnly" :readonly="isViewMode" :text="$t('text.onlyStartingPassAllowed')"></c-checkbox>
			</v-col></v-row>
			<v-row><v-col>
				<v-autocomplete outlined dense
					hide-details="auto"
					:readonly="isViewMode"
					v-model="category.selected"
					:items="competitionCategory"
					:label="$t('text.category')"
					:error-messages="errors('category')"
					:rules="[rules.noEmpty]"
					required
					@input="$v.category.selected.$touch()"
					@blur="$v.category.selected.$touch()">
				</v-autocomplete>
			</v-col></v-row>
			<v-row><v-col>
				<v-autocomplete outlined dense
					hide-details="auto"
					:readonly="isViewMode"
					v-model="kind.selected"
					:items="competitionKind"
					:label="$t('text.kind')"
					:error-messages="errors('kind')">
				</v-autocomplete>
			</v-col></v-row>
            </v-form>
            <spinner :loading="loading"></spinner>
        </template>
		<template #pageActions>
			<v-row>
				<v-col>
					<v-btn
						class="mr-4"
						v-if="isViewMode"
						@click="backToOverview">
						{{ $t(`text.backToOverview`) }}
					</v-btn>
					<v-btn
						class="mr-4"
						v-if="isViewMode"
						@click="saveAndNext">
						{{ $t(`text.next`) }}
					</v-btn>
					<v-btn
						class="mr-4"
						v-if="!isViewMode"
						:disabled="loading"
						@click="save">
						{{ $t(`text.save`) }}
					</v-btn>
					<v-btn
						class="mr-4"
						v-if="!isViewMode"
						:disabled="loading"
						@click="saveAndNext">
						{{ $t(`text.createAndNext`) }}
					</v-btn>

					<v-btn
						:disabled="loading"
						class="mr-4"
						@click="back">
						{{ $t(`text.backButton`) }}
					</v-btn>
				</v-col>
			</v-row>
        </template>
    </page>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import { requiredIf } from 'vuelidate/lib/validators';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';
import CCheckbox from '../general/CCheckbox.vue';

import compApprovalActions from '../store/actions/compApproval';
import namespace from '../store/namespace';

import getCompetitionCategory from '../models/competitionCategory';
import getCompetitionKind from '../models/competitionKind';
import modelState from '../mixins/modelState';

export default {
	name: 'CategoryArt',
	mixins: [modelState],
	components: {
		Page,
		Spinner,
		CCheckbox,
	},
	validations: {
		category: {
			selected: {
				// eslint-disable-next-line func-names
				required: requiredIf(function () {
					return this.isCategoryRequired;
				}),
			},
		},
	},
	data() {
		return {
			startPassOnly: false,
			category: {
				selected: 16,
			},
			kind: {
				selected: 0,
			},
			rules: {
				noEmpty: v => !(v === 0 && this.isCategoryRequired) || this.$i18n.t('text.errorEmpty'),
			},
			valid: true,
			loading: false,
		};
	},
	async mounted() {
		this.loading = true;
		if (this.CompApprLinks.length === undefined || this.CompApprLinks.length === 0) {
			await this.GET_COMP_APPROVAL_LINKS();
		}
		if (!this.Competition || this.Competition.id !== this.cGuid) {
			const link = this.CompApprLinks.find(x => x.rel === 'get_by_id');
			if (link) {
				await this.GET_COMP_BY_ID({ link, cGuid: this.cGuid });
				this.readFromDTO();
			}
		} else {
			this.readFromDTO();
		}
		this.loading = false;
	},
	computed: {
		...mapGetters(['WindowSize']),
		...mapGetters(namespace.COMPETITIONAPPROVAL, ['CompApprLinks', 'Competition']),
		competitionCategory() {
			return getCompetitionCategory(this, 1);
		},
		competitionKind() {
			return this.Competition ? getCompetitionKind(this, this.Competition.competitionApprovalType) : [];
		},
		cGuid() {
			return this.$route.params.cGuid || null;
		},
		isCategoryRequired() {
			return this.startPassOnly;
		},
		isViewMode() {
			return this.$route.name === 'showcategoryArt';
		},
	},
	watch: {
		startPassOnly(val) {
			if (!val) {
				this.$refs.form.resetValidation();
			} else {
				this.$refs.form.validate();
			}
		},
		'category.selected': function () {
			this.$refs.form.validate();
		},
	},
	methods: {
		...mapActions(namespace.COMPETITIONAPPROVAL, [
			compApprovalActions.GET_COMP_APPROVAL_LINKS,
			compApprovalActions.GET_COMP_BY_ID,
			compApprovalActions.UPDATE_COMP_APPROVAL,
		]),
		errors(name) {
			const errors = [];
			const apiErrors = this.validateProperty(name);
			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			return errors;
		},
		async create() {
			this.$v.$touch();

			if (!this.$v.$invalid) {
				let link = null;
				link = this.CompApprLinks.find(x => x.rel === 'update_comp');
				this.Competition.onlyWithLicense = this.startPassOnly;
				this.Competition.competitionCategory = this.category.selected;
				this.Competition.competitionSubType = this.kind.selected;

				if (link) {
					try {
						this.modelState = {};
						await this.UPDATE_COMP_APPROVAL({ newCompApproval: this.Competition, link, cGuid: this.cGuid });
					} catch (e) {
						console.log(e);
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
		},
		async save() {
			this.loading = true;
			this.$refs.form.validate();
			if (!this.$v.$invalid && this.valid) {
				await this.create();
				this.$router.push({ name: 'competitionapproval' });
			} else if (!this.valid && this.$refs.form.errorBag) {
				const id = Object.entries(this.$refs.form.errorBag).find(x => x[1] == true)[0];
				if (id) document.getElementById(`input-${id}`).scrollIntoView();
			}
			this.loading = false;
		},
		async saveAndNext() {
			this.loading = true;
			if (!this.isViewMode) {
				this.$refs.form.validate();
				if (!this.$v.$invalid && this.valid) {
					await this.create();
					this.$router.push({
						name: 'compAppEventClasses',
						params: { cGuid: this.cGuid },
					});
				} else if (!this.valid && this.$refs.form.errorBag) {
					const id = Object.entries(this.$refs.form.errorBag).find(x => x[1] == true)[0];
					if (id) document.getElementById(`input-${id}`).scrollIntoView();
				}
			} else {
				this.$router.push({
					name: 'showcompAppEventClasses',
					params: { cGuid: this.cGuid },
				});
			}
			this.loading = false;
		},
		backToOverview() {
			this.$router.push({
				name: 'competitionapproval',
			});
		},
		back() {
			if (this.isViewMode) {
				this.$router.go(-1);
			} else {
				this.$router.push({
					name: 'editcompetitionapproval',
					params: { cGuid: this.cGuid },
				});
			}
		},
		readFromDTO() {
			this.startPassOnly = this.Competition.onlyWithLicense;
			this.category.selected = this.Competition.competitionCategory || 16;
			this.kind.selected = this.Competition.competitionSubType;
		},
	},
};
</script>
